import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';

/**
 * Pre-initialized MSAL instance, set by main.ts before Angular bootstraps.
 * MSAL Browser v4 requires explicit `await pca.initialize()` before any API call.
 */
let _msalInstance: IPublicClientApplication | null = null;

export function setMsalInstance(instance: IPublicClientApplication): void {
  _msalInstance = instance;
}

export async function createAndInitializeMsal(): Promise<IPublicClientApplication> {
  const leanbyte = (window as any)['leanbyte'];
  const oAuthConfig = leanbyte?.oAuthConfig || {};

  // Always use the current origin as redirect URI — the backend's "redirect"
  // field points to the production deployment URL, not localhost.
  const redirectUri = window.location.origin;

  // Backend config uses "cacheLocation" as a string ("sessionStorage" | "localStorage").
  const cacheLocation = oAuthConfig.cacheLocation === 'localStorage'
    ? BrowserCacheLocation.LocalStorage
    : BrowserCacheLocation.SessionStorage;

  const pca = new PublicClientApplication({
    auth: {
      clientId: oAuthConfig.clientId || '',
      authority: oAuthConfig.authority || '',
      redirectUri,
      // KCS-565 Part 2: don't navigate back to the URL that initiated login. Default (true) returns the
      // browser to wherever login started — which, now that staff start it from the /login hub button, is
      // /#/login, leaving a freshly-authenticated user stranded on the login page. Staying at the redirect
      // URI (origin) lets the router land on '/' → the guard sees the account → dashboard.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation,
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });

  await pca.initialize();

  // Complete MSAL redirect flow — sets active account after returning from Microsoft login
  const resp = await pca.handleRedirectPromise();
  if (resp !== null) {
    pca.setActiveAccount(resp.account);
  }

  return pca;
}

export function MSALInstanceFactory(): IPublicClientApplication {
  if (_msalInstance) return _msalInstance;
  throw new Error('MSAL instance not initialized. Ensure main.ts calls createAndInitializeMsal() before bootstrapping.');
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const leanbyte = (window as any)['leanbyte'];
  const clientId = leanbyte?.oAuthConfig?.clientId || '';

  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  // Backend API requires a token scoped to the app's own clientId, not Microsoft Graph
  protectedResourceMap.set('/api/*', [`${clientId}/.default`]);
  // Knowledge Graph FastAPI — same clientId scope, just on a different host (dev) or path (prod).
  // MsalInterceptor matches outgoing HttpClient calls against this URL pattern and attaches a JWT.
  protectedResourceMap.set(`${environment.kgApiBaseUrl}/api/*`, [`${clientId}/.default`]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  };
}
